import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { audioPath } from 'constants/audioConstants';
import {
  MULTI_RUN_VOTING_PREFIX,
  PLAYER_PRE_ACTION,
  TABLE_TYPE_CASH,
  TABLE_TYPE_SITNGO,
  TABLE_TYPE_TOURNAMENT
} from 'constants/gameConstants';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import GameContext from 'contexts/GameContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { useOrientation } from 'hooks/useOrientation';
import useSound from 'hooks/useSound';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import {
  actionAppearsDropped,
  pendingActionOutcome,
  turnIdentity,
  TurnSnapshot,
  turnStamp
} from 'utils/actionOutcome';
import { currencyFormatter, toLocaleStringNumber } from 'utils/functions';
import type { GuardrailTable } from 'utils/paceGuardrails';
import { reportActionNotApplied, reportActionTiming } from 'utils/paceGuardrails';

import { useServerTimeout } from 'components/UserActions/ServerTimeoutBanner';
import StraddlePreActionButton, {
  useStraddlePreActionOffer
} from 'components/UserActions/TableActions/StraddlePreActionButton';

import CallAction from './CallAction';
import CheckAction from './CheckAction';
import CheckboxAction from './CheckboxAction';
import Fold from './FoldAction';
import MuckCard from './MuckCard';
import RaiseAction from './RaiseAction';
import ShowCard from './ShowCard';

import 'components/UserActions/UserActions.scss';
import './PokerActions.scss';

// How often the client re-checks a held action that has produced no effect, and
// the minimum spacing between two sends of it. Derived per table from the
// player's own turn clock (see actionRetryIntervalMs); these clamp it.
//
// The floor must stay clear of the 2s emit ack timeout in GameService, so a
// re-send never overlaps an ack that is still legitimately on its way. The cap
// keeps a long clock from turning into a long wait: on a 500s table the check
// still runs every 3s.
const ACTION_RETRY_MAX_MS = 3000;
const ACTION_RETRY_MIN_MS = 2500;

// After re-asserting the table's subscription, how long to give the fresh frame
// to arrive before deciding the turn really is unchanged and re-sending.
//
// 1500, not 1000: the measured frame cadence is p50 1005ms / p95 1209ms, so a
// 1s settle sits inside the p95 gap and roughly the slowest 5% of snapshots land
// just after it - costing one extra get_playermove on the wire for an action
// that was about to be confirmed. Raised in PR review; 1500 clears the measured
// p95 without meaningfully extending how long the row is held.
const RESYNC_SETTLE_MS = 1500;

// Total sends allowed for ONE committed action, across EVERY trigger.
//
// A hard budget, not a per-trigger one. An earlier version capped only the
// periodic no-effect check and documented the event triggers as "still honoured
// after it", reasoning that each event is fresh evidence a send might land. An
// ack timeout that keeps firing is not fresh evidence - it is the same failure
// repeating - so the ack-timeout path was uncapped: send, time out at 2s,
// re-send, time out, for the whole turn. On a 500s table that is well over a
// hundred emits for one decision (PR review finding 1).
//
// A genuine action-socket recovery IS new information, so that transition adds
// to the budget rather than being exempt from it - but only so many times. A
// flapping action socket produces a down/ok transition per cycle, and an
// uncapped bonus per cycle made the "hard budget" soft: a measured 21 emits for
// one action across eight offline windows (PR review). The ceiling is what makes
// the bound actually hard.
const MAX_DELIVERY_ATTEMPTS = 6;
const PATH_RECOVERY_BONUS = 1;
const MAX_PATH_RECOVERY_BONUS = 3;
const MAX_BUDGET = MAX_DELIVERY_ATTEMPTS + MAX_PATH_RECOVERY_BONUS;

// While an action is held with its send budget spent, how often to keep
// re-asserting the table's subscription, and how many times.
//
// Sized against the PRODUCTION clock, not the long clocks in lower
// environments. At the 17s clock live tables use, actionRetryIntervalMs clamps
// to 3000, so the 6-send budget spends at roughly 18s - about when the server
// clock expires. If the turn identity has STILL not changed by then, the client
// is not receiving this table's frames at all, which is the EN-10628 failure
// itself: re-asserting the feed is the only remedy left, and it is what lets the
// client learn the outcome and tell the player.
//
// Capped rather than open-ended, and capped at the handoff. The per-table
// watchdog owns a dead feed from here on, but its worst-case first detection is
// silenceMs + jitter = 30s, which is longer than the whole turn - so these
// cover the gap between the budget running out and the watchdog acting, then
// stop. MAX_HELD_RESYNCS is the TOTAL for one action across every trigger that
// can ask for a re-assert, not a per-trigger cap, so the worst case really is
// three extra subscribe calls per held action.
const HELD_RESYNC_INTERVAL_MS = 10_000;
const MAX_HELD_RESYNCS = 3;

type RecoveryTrigger = 'no-effect' | 'ack-timeout' | 'path-recovered';

/** Everything the client holds about an action the player has committed to. */
type PendingAction = {
  /** The action name as the engine will record it: fold, check, call, raise, bet, allin. */
  name: string;
  /** Re-sends exactly the action the player chose. */
  send: () => void;
  /** The turn state the action was submitted into. */
  submitted: TurnSnapshot;
  firstSentAt: number;
  lastSentAt: number;
  /** Sends made for this action, including the original. */
  attempts: number;
  /** Hard ceiling on `attempts`. A socket recovery raises it. */
  budget: number;
  /** So the "budget spent" line is logged once, not once per trigger. */
  budgetSpentLogged?: boolean;
  /** Local time after which the turn this action was made on is over. */
  turnDeadline: number;
};

// The engine still accepts a move this long after the turn clock runs out
// (SECONDS_BYPASS_DELAY in apps/engine/games/common/constants.js).
const TURN_LAG_ALLOWANCE_S = 3;

/**
 * When the turn an action was made on ends, on the local clock: the local time
 * the frame's server clock (`time`) arrived, plus what the turn had left at
 * that moment (`speed - (time - lastmove)`). Anchored to the frame, not to the
 * tap, so the seconds a player spends deciding do not extend it, and device
 * clock skew never enters. `lastmove` can be a second ahead of `time` (the
 * engine stamps `time + 1`), which correctly pushes the deadline out. Infinity
 * while the turn clock is unknown, so a missing `speed` never cuts a send short.
 */
const getTurnDeadline = (
  frameSeenAt: number,
  time: unknown,
  lastmove: unknown,
  speed: unknown
) => {
  const clockS = Number(speed);
  if (!(clockS > 0)) return Infinity;
  const elapsedMs = (Number(time) - Number(lastmove)) * 1000 || 0;
  return frameSeenAt + (clockS + TURN_LAG_ALLOWANCE_S) * 1000 - elapsedMs;
};

const PokerActions = () => {
  const { setIsTimerOff } = useContext(GameContext);
  const { actionPathStatus, resyncTable, setPriorityTable } = useContext(ConnectionManagerContext);
  const { currentPlayer, players, game } = useTableSelector((table) => table);

  const { isFolded: isCurrentUserFolded, seatId } = currentPlayer;

  const { hand, runs } = game;
  const {
    allIn,
    bet,
    call,
    check,
    preCall,
    preCallAny,
    raise,
    isMyTurn,
    muckCard,
    playerCallAmount,
    showAction,
    showCard
  } = useTableSelector((table) => table.gameState.pokerActions);
  const straddleOffer = useStraddlePreActionOffer();
  const [preselectedAction, setPreselectedAction] = useState((game as any)[`p${seatId}preAction`]);
  const playerAction = (game as any)[`p${seatId}action`];
  // Raising and hiding the banner belongs to ServerTimeoutProvider, one level
  // up in UserActions. It used to be local state here, which meant the banner
  // could not be shown while any OTHER action panel held the slot — the
  // run-it-twice vote window above all (see the provider for the full case).
  const { showServerTimeout, hideServerTimeout } = useServerTimeout();

  // Reset timer when turn changes (next server event after player acted)
  useEffect(() => {
    if (!isMyTurn) {
      setIsTimerOff(false);
    }
  }, [isMyTurn, setIsTimerOff]);

  useEffect(() => {
    setPreselectedAction((game as any)[`p${seatId}preAction`]);
  }, [
    game.p1preAction,
    game.p2preAction,
    game.p3preAction,
    game.p4preAction,
    game.p5preAction,
    game.p6preAction,
    game.p7preAction,
    game.p8preAction,
    game.p9preAction
  ]);

  // "It's your turn" cue (Unity parity: UIPlayer.OnPlayerMoveStarted plays
  // players_turn.wav once on the transition into the local player's turn). The
  // asset already exists in audioConstants but was never wired up. Howler's
  // global mute (GlobalContext.isMuted) applies to use-sound clips, so no extra
  // mute gate is needed.
  // playOnArrival: this is the one table cue that is NOT a replay of something
  // that already happened — it announces the state the player is arriving into,
  // and switching to a table where it is your turn is precisely when you need to
  // hear it. Every other cue is snapped on arrival (see hooks/useSound).
  // crossTable: a turn starting on a table you are NOT looking at is exactly
  // the cue that has to reach you (EN-10577 AC2). Not queued behind other
  // tables — it is one edge-triggered cue, and two tables announcing a turn in
  // quick succession is informative rather than chaotic; only the per-second
  // countdown is exclusive (AC7).
  const [playPlayersTurn] = useSound(audioPath.playersTurn, {
    playOnArrival: true,
    crossTable: true
  });
  // AM I THE PANEL THE PLAYER IS LOOKING AT? Used by the server-timeout banner
  // below, which portals to <body> and so escapes the `visibility: hidden` that
  // hides a kept-alive carousel panel (#3720 re-review).
  //
  // #3720 had to AND this with a grid check, because grid cells had no
  // TableActiveProvider and the context defaults to true. EN-10577 gives every
  // cell one, so the context answers for both surfaces on its own and the grid
  // clause is redundant.
  const isActiveTable = useIsActiveTable();
  // YOUR TURN CROSSES TABLES (EN-10577 AC2), so it deliberately does NOT use
  // the gate above. It fired only for the active table before, on the reasoning
  // that the orange turn-glow was the visual cue. But a turn on a table the
  // player is NOT watching is precisely what they need told, and missing it
  // costs them money; CX-1442 is a player reporting that gap on Unity.
  const shouldCueTurn = isMyTurn;
  const prevShouldCueTurnRef = useRef(false);
  useEffect(() => {
    // Edge-trigger: fire once on the rising edge (turn starts on the active
    // table, or the player selects a table where it's already their turn), never
    // on the turn ending. Mirrors Unity's playerTurnSoundPlayed one-shot guard.
    // Dedupe belongs to useSound (see WrapperOptions.dedupeKey): claiming here
    // spends the claim even when the play is then gated, and the table's other
    // mounted copy is refused — the cue is simply lost.
    if (shouldCueTurn && !prevShouldCueTurnRef.current) {
      playPlayersTurn({ dedupeKey: 'turn' });
    }
    prevShouldCueTurnRef.current = shouldCueTurn;
  }, [shouldCueTurn, playPlayersTurn, game.gameID]);

  const { gameEnding, notFoldedPlayers } = useTableSelector((table) => table.gameState);
  const playerStatus = players[+seatId]?.status;
  const playerStack = players[+seatId]?.stack;
  const gameHand = +hand;
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);

  const { playerTotalChips, playersStack } = useTableSelector(
    (table) => table.gameState.pokerActions.betMultiplier
  );

  const { preselectAction } = useGameService();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';

  const tableType = useTableSelector((table) => table.game.tabletype);
  // Seconds; the same value the seat's countdown ring counts against
  // (SeatTimer `target` via SeatWithPlayer). 0 until the table state lands.
  const timerSpeed = useTableSelector((table) => table.game.speed);

  // An action the player has committed to is IN FLIGHT until the turn moves on.
  // While it is, the action row is hidden - not greyed out. That is what the
  // Unity client players came from did (TableView hides TurnActionButtons on
  // press), and a greyed row reads as broken. It is also the strongest
  // double-submit guard there is: a second tap hits nothing.
  //
  // It is never brought back during the same turn. The client holds the
  // player's decision and delivers it itself (below); a re-shown row could only
  // invite a second, different decision on the same spot, and a manual re-tap
  // sends exactly the same emit a re-send does, so it has no recovery value.
  const [isActionInFlight, setIsActionInFlight] = useState(false);
  /** An action is held but its send budget is spent. Drives the held-resync effect. */
  const [heldWithoutSends, setHeldWithoutSends] = useState(false);

  // The maintenance lockout is a POLICY on tournament and SnG tables, not an
  // in-flight lock, and holding both in one flag is a bug: the in-flight
  // release below clears whatever it finds set, so it used to cancel the
  // maintenance lockout too - the row went live partway into a maintenance
  // window, re-locked on the next turn, released again, and flapped for the
  // whole window while the platform meant to be blocking those actions.
  // A policy the player should SEE stays visible and disabled; an in-flight
  // action is hidden. Different states, different presentation.
  const isMaintenanceLocked = isMaintenance && tableType !== TABLE_TYPE_CASH;

  // Half the player's own turn clock, clamped. A missing/zero speed must not
  // read as "check immediately", so it falls back to the cap.
  const actionRetryIntervalMs = useMemo(() => {
    const clockMs = (+timerSpeed || 0) * 1000;
    if (clockMs <= 0) return ACTION_RETRY_MAX_MS;
    return Math.max(ACTION_RETRY_MIN_MS, Math.min(ACTION_RETRY_MAX_MS, clockMs / 2));
  }, [timerSpeed]);
  // The banner itself is idempotent and self-hiding; the only part that is
  // this component's business is dropping a pre-action that is no longer ours
  // to hold.
  const triggerShowServerTimeout = useCallback(() => {
    if (!isMyTurn) {
      setPreselectedAction(null);
    }

    showServerTimeout();
  }, [isMyTurn, showServerTimeout]);

  const triggerHideServerTimeout = hideServerTimeout;

  const onClickPreSelectAction = (action: string) => {
    preselectAction(action, triggerHideServerTimeout, triggerShowServerTimeout);
    setPreselectedAction(action);
  };

  const actionsRef = useRef<HTMLDivElement>(null);

  // The turn state as the engine currently reports it. The in-flight release and
  // the re-send decision are both expressed against this one value so they
  // cannot disagree about what "the turn moved on" means. See utils/actionOutcome
  // for why `lastmove` is the field that makes it trustworthy.
  const currentTurn: TurnSnapshot = useMemo(
    () => ({
      gameNo: game.gameNo,
      hand: gameHand,
      move: game.move,
      playerAction,
      lastmove: game.lastmove,
      bet: game.bet
    }),
    [game.gameNo, gameHand, game.move, playerAction, game.lastmove, game.bet]
  );
  const currentTurnId = turnIdentity(currentTurn);
  // Latest turn state, readable from inside a timeout without becoming an effect
  // dependency. The recovery timers must be armed per SEND, not per table
  // update, or a busy table re-arms them forever (see the perf notes in
  // docs/investigations/20260819-game-client-overheating.md).
  const currentTurnRef = useRef<TurnSnapshot>(currentTurn);
  currentTurnRef.current = currentTurn;
  const isMyTurnRef = useRef(isMyTurn);
  isMyTurnRef.current = isMyTurn;
  // EN-11182: who and where, for the pace-guardrail events (utils/paceGuardrails).
  const guardrailTable: GuardrailTable = {
    playerGUID: currentPlayer.playerGUID || '',
    gameID: game.gameID,
    game: game.game,
    tabletype: game.tabletype,
    pace: game.handPace
  };
  const guardrailTableRef = useRef(guardrailTable);
  guardrailTableRef.current = guardrailTable;
  // When the current turn of ours reached this client (null when it is not
  // ours), for the fast-action proxy.
  const turnSeenAtRef = useRef<number | null>(null);
  // The turn clock, and the local time the current server `time` first arrived
  // (see getTurnDeadline). `seenAt` only moves when `time` does.
  const turnClockRef = useRef({ time: game.time, seenAt: Date.now(), speed: timerSpeed });
  if (turnClockRef.current.time !== game.time) {
    turnClockRef.current.time = game.time;
    turnClockRef.current.seenAt = Date.now();
  }
  turnClockRef.current.speed = timerSpeed;

  // Put this table on the feed watchdog's tighter clock while it is our turn.
  //
  // The watchdog's 20s silence threshold was sized for BACKGROUND tables, on the
  // reasoning that the acting table is covered by this component's own re-assert
  // at ~3s. That only holds once the player has pressed something: a feed that
  // dies on the turn they are about to take has nothing watching it, so it took
  // 20-35s to notice - two or more missed turns at the production clock. On the
  // acting table the threshold drops to ~6s (see DEFAULT_PRIORITY_SILENCE_MS).
  //
  // Gated on isActiveTable as well as isMyTurn so exactly one table can hold it:
  // in the grid it can be our turn on several tables at once, and the point of
  // the tighter clock is that it costs one request per client.
  useEffect(() => {
    if (!isMyTurn || !isActiveTable) return undefined;
    setPriorityTable(game.gameID);
    return () => setPriorityTable(null);
  }, [isMyTurn, isActiveTable, game.gameID, setPriorityTable]);

  // -- The action the player has committed to, and how to deliver it. --
  //
  // A Socket.IO ack does not tell us it landed: the engine acks `get_playermove`
  // BEFORE it validates or even enqueues anything, and its rejection paths are
  // bare returns with no reply. So the client watches for the action's EFFECT
  // (utils/actionOutcome) and, until the turn moves on, treats the action as its
  // job to deliver. It re-sends on every reason to believe a send can land now:
  //
  //   - the ack never came (GameService's 2s emit timeout);
  //   - the action socket came back after being down;
  //   - the table state has not moved for a retry interval, in which case the
  //     table's subscription is re-asserted first (a dead feed is the likelier
  //     fault than a dead send) and the re-send waits for the fresh frame.
  //
  // Re-sending is safe for a specific reason rather than by assumption. The
  // engine applies a move only when it is that seat's turn
  // (`if (playerId != gameTable.move) return;` in games/texas/playerMove.js), so
  // if an original and a re-send both arrive, whichever applies first advances
  // the turn and the other is rejected. Identical payloads also collapse to one
  // member in the engine's Redis move queue. And the client only re-sends while
  // the turn IDENTITY is unchanged, checked immediately before each send - so
  // the re-send targets exactly the decision the player made, never a later
  // turn's.
  const pendingRef = useRef<PendingAction | null>(null);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every send, including a re-send, so the no-effect check below is
  // armed once per send rather than once per table update.
  const [sendSeq, setSendSeq] = useState(0);

  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  /**
   * Re-asserts this table's subscription for a held action whose send budget is
   * spent, under a single allowance shared by EVERY trigger.
   *
   * One allowance, for the same reason the send budget is one budget. Moving the
   * re-subscribe above the budget check (so the tick that discovers the budget
   * is spent still performs the last-chance repair) removed the only thing
   * bounding it: once the budget is spent `scheduleRecovery` returns before
   * arming `recoveryTimerRef`, so that guard is always null and every
   * action-socket transition earned another subscribe Lambda invocation and
   * DynamoDB row rewrite - measured at 32 for one action across twenty flaps
   * (PR review). The interval's own cap governed only the interval.
   *
   * The allowance is per ACTION, not per budget window: it is deliberately NOT
   * reset when a recovery raises the send budget, which would have multiplied it
   * by the number of bonuses.
   */
  const heldResyncsRef = useRef(0);
  const resyncHeldTable = useCallback(
    (reason: string) => {
      const pending = pendingRef.current;
      if (!pending) return;
      if (heldResyncsRef.current >= MAX_HELD_RESYNCS) return;
      heldResyncsRef.current += 1;
      console.warn('[PokerActions] held action, budget spent - re-asserting table only', {
        gameID: game.gameID,
        action: pending.name,
        reason,
        resync: heldResyncsRef.current,
        heldForMs: Date.now() - pending.firstSentAt
      });
      resyncTable(game.gameID);
    },
    [resyncTable, game.gameID]
  );

  /**
   * Give the player their controls back.
   *
   * The committed-action model says the turn moving on is the only thing that
   * ends an in-flight action, and for a client that is receiving this table's
   * frames that is right: the turn always moves, and the first frame of the new
   * state says what became of the action. This is the case where that premise
   * fails - the client is not receiving the frames, so the turn never appears to
   * move, and "hold until it does" becomes "hold forever".
   *
   * That state is the worst one available to a player: no action row, no seat
   * clock (the buttons stop it on press), and no message. QA hit it on both
   * platforms on this build, and the develop logs show why it is not theoretical:
   *
   *  - omaha3 11:07:48Z - the player's `raise 36` was APPLIED on the first send,
   *    and the client re-sent it five more times over the next 21s, straight
   *    through three other players' turns, because its own feed was dead and the
   *    turn identity never moved. Row hidden throughout.
   *  - texas673023 11:39:54Z - the player's `call` reached the engine on none of
   *    its six sends (acked every time; the engine acks before it validates), and
   *    the row stayed hidden for the 40s until the socket recycled.
   *
   * Re-showing the row was declined in earlier review on the grounds that a
   * legitimate client can never have an action refused, so a held action always
   * lands or the clock takes the hand. Both halves of that hold. Neither covers
   * this: the action is not refused, it is unobserved, and the clock taking the
   * hand is something the client only learns from the frames it is not getting.
   *
   * Releasing is safe in the way the hiding was meant to be. The pending action
   * is dropped in the same breath, so afterwards there is exactly ONE live path
   * to the engine - the player's own tap - which is the double-submit hazard the
   * hidden row existed to prevent. And a late duplicate is already inert: the
   * engine applies a move only when `playerId == gameTable.move`.
   *
   * A player who does get their row back and re-taps starts a fresh budget. That
   * is not a loop: one cycle costs the whole budget (~21s at the production
   * clock, longer than the turn), and it is the player's own deliberate second
   * decision on a turn the client can no longer describe to them.
   */
  const releaseHeldAction = useCallback(
    (reason: string) => {
      const pending = pendingRef.current;
      if (!pending) return;
      // If the turn HAS moved we have no business here - the turn-identity effect
      // owns that path, and it is the one that can say whether the action landed.
      if (!actionAppearsDropped(pending.submitted, currentTurnRef.current, isMyTurnRef.current)) {
        return;
      }
      console.warn('[PokerActions] action-abandoned - restoring the row', {
        gameID: game.gameID,
        seat: seatId,
        action: pending.name,
        reason,
        attempts: pending.attempts,
        resyncs: heldResyncsRef.current,
        heldForMs: Date.now() - pending.firstSentAt,
        turn: turnIdentity(pending.submitted)
      });
      reportActionNotApplied({
        ...guardrailTableRef.current,
        turn: turnIdentity(pending.submitted),
        outcome: 'abandoned',
        action: pending.name,
        attempts: pending.attempts,
        heldForMs: Date.now() - pending.firstSentAt
      });
      pendingRef.current = null;
      clearRecoveryTimer();
      setIsActionInFlight(false);
      setHeldWithoutSends(false);
      // We are the ones who set this on press (ActionButton), so releasing it is
      // ours to do - the same `if (pending)` reasoning as the turn-identity
      // effect, which must not steal Player.tsx's rebuy hold.
      setIsTimerOff(false);
      // The player is being handed a decision back; say why the first one could
      // not be confirmed. Idempotent per turn, so an ack timeout that already
      // raised it does not re-arm the auto-hide.
      triggerShowServerTimeout();
    },
    [game.gameID, seatId, clearRecoveryTimer, setIsTimerOff, triggerShowServerTimeout]
  );

  /**
   * Submit a player action, keeping hold of how to re-send it.
   *
   * Every action button routes its click through here, so the recovery policy
   * lives in one place instead of six components. `name` is the action as the
   * engine will record it, used afterwards to tell "landed" from "the clock
   * acted for the player".
   */
  const submitAction = useCallback(
    (send: (stamp: string) => void, name: string) => {
      const now = Date.now();
      clearRecoveryTimer();
      // The stamp is fixed here, from the frame the player acted on, and every
      // re-send reuses it. Reading it at re-send time instead could pick up a
      // frame that landed after the turn-unchanged check, giving a stale action
      // the new turn's stamp - the one copy the engine must refuse (EN-11171).
      const submitted = { ...currentTurnRef.current };
      const stamp = turnStamp(submitted);
      const sendStamped = () => send(stamp);
      reportActionTiming({
        ...guardrailTableRef.current,
        turn: turnIdentity(submitted),
        action: name,
        msSinceTurn: turnSeenAtRef.current === null ? null : now - turnSeenAtRef.current
      });
      pendingRef.current = {
        name,
        send: sendStamped,
        submitted,
        firstSentAt: now,
        lastSentAt: now,
        attempts: 1,
        budget: MAX_DELIVERY_ATTEMPTS,
        turnDeadline: getTurnDeadline(
          turnClockRef.current.seenAt,
          turnClockRef.current.time,
          submitted.lastmove,
          turnClockRef.current.speed
        )
      };
      setIsActionInFlight(true);
      setHeldWithoutSends(false);
      setSendSeq((n) => n + 1);
      sendStamped();
    },
    [clearRecoveryTimer]
  );

  /**
   * Re-send the held action, if there still is one and the turn has not moved.
   * The identity check happens HERE, at send time, not when the send was
   * scheduled.
   */
  const sendPending = useCallback(
    (trigger: RecoveryTrigger): void => {
      const pending = pendingRef.current;
      if (!pending) return;
      if (!actionAppearsDropped(pending.submitted, currentTurnRef.current, isMyTurnRef.current)) {
        return;
      }
      // The turn this action was made on is over, whatever the (possibly frozen)
      // feed still shows. Sending it now can only be refused, or applied to a
      // turn the player never saw (EN-11171), so hand the row back instead.
      if (Date.now() > pending.turnDeadline) {
        releaseHeldAction('turn-expired');
        return;
      }
      pending.attempts += 1;
      pending.lastSentAt = Date.now();
      console.warn('[PokerActions] re-sending held action', {
        gameID: game.gameID,
        action: pending.name,
        trigger,
        attempt: pending.attempts,
        heldForMs: pending.lastSentAt - pending.firstSentAt,
        turn: turnIdentity(pending.submitted)
      });
      setSendSeq((n) => n + 1);
      pending.send();
    },
    [game.gameID, releaseHeldAction]
  );

  /**
   * Schedule ONE re-send of the held action. One outstanding at a time, never
   * closer than the retry interval to the previous send, and after a
   * re-subscribe, not before the fresh frame has had a chance to arrive.
   *
   * The re-subscribe comes first on purpose. If this table's feed is dead the
   * player is not being told the turn already moved, and re-sending into that
   * only earns a harmless "not your turn" discard; the re-subscribe is what
   * fixes it. If the feed is fine and the send was what failed, the settle
   * costs one second and the re-send still goes out.
   */
  const scheduleRecovery = useCallback(
    (trigger: RecoveryTrigger, resync: boolean) => {
      const pending = pendingRef.current;
      if (!pending || recoveryTimerRef.current) return;
      if (!actionAppearsDropped(pending.submitted, currentTurnRef.current, isMyTurnRef.current)) {
        return;
      }
      // The send budget applies to every trigger, which is the point (see
      // MAX_DELIVERY_ATTEMPTS). Reaching it does NOT end the feed remedy: that
      // is a different repair for a different half of the failure, and a dead
      // per-connection fan-out row stays dead until something re-asserts it. So
      // the spent branch still repairs - it just does so under the held
      // allowance rather than unbounded (see resyncHeldTable).
      if (pending.attempts >= pending.budget) {
        if (resync) resyncHeldTable(trigger);
        setHeldWithoutSends(true);
        if (!pending.budgetSpentLogged) {
          pending.budgetSpentLogged = true;
          console.warn('[PokerActions] delivery budget spent - holding without further sends', {
            gameID: game.gameID,
            action: pending.name,
            attempts: pending.attempts,
            heldForMs: Date.now() - pending.firstSentAt
          });
        }
        return;
      }
      if (resync) resyncTable(game.gameID);
      const sinceLastSend = Date.now() - pending.lastSentAt;
      const spacing = Math.max(0, actionRetryIntervalMs - sinceLastSend);
      const delay = Math.max(spacing, resync ? RESYNC_SETTLE_MS : 0);
      recoveryTimerRef.current = setTimeout(() => {
        recoveryTimerRef.current = null;
        sendPending(trigger);
      }, delay);
    },
    [resyncTable, resyncHeldTable, game.gameID, actionRetryIntervalMs, sendPending]
  );

  /**
   * The emit was not acked within GameService's timeout, or could not be sent
   * at all. Shows the banner exactly as main does on this event, and queues a
   * re-send: the client knows what the player chose, so it does not hand the
   * decision back to them.
   */
  const onAckTimeout = useCallback(() => {
    triggerShowServerTimeout();
    scheduleRecovery('ack-timeout', false);
  }, [triggerShowServerTimeout, scheduleRecovery]);

  // The turn moved on. This is the ONLY thing that ends an in-flight action.
  //
  // Keyed on the turn IDENTITY, not the rising edge of isMyTurn (EN-10628). The
  // edge is only observable if the client receives the frame where isMyTurn goes
  // false, and on a constrained link or across back-to-back turns that frame can
  // be coalesced away - leaving isMyTurn reading true, then true, so an
  // edge-triggered effect never re-runs. The identity differs across two turns
  // whether or not the frame between them arrived, because the engine stamps
  // `lastmove` on every turn advance.
  //
  // When a held action ends this way, the first frame of the new state says what
  // became of it. If the engine's record of our seat is a fold (or a check where
  // we meant to bet), the clock acted for the player and they are told, via the
  // banner that already exists on main. Anything else is logged and dropped.
  // The pending action is never carried into the new turn.
  //
  // Maintenance is deliberately absent here: it is enforced by
  // isMaintenanceLocked at the buttons, so it cannot be cleared by anything that
  // releases the in-flight state.
  const lastTurnIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastTurnIdRef.current === currentTurnId) return;
    lastTurnIdRef.current = currentTurnId;
    turnSeenAtRef.current = isMyTurnRef.current ? Date.now() : null;

    const pending = pendingRef.current;
    if (pending) {
      const outcome = pendingActionOutcome(pending.name, currentTurnRef.current.playerAction);
      const detail = {
        gameID: game.gameID,
        seat: seatId,
        action: pending.name,
        recorded: currentTurnRef.current.playerAction,
        outcome,
        attempts: pending.attempts,
        heldForMs: Date.now() - pending.firstSentAt,
        submittedTurn: turnIdentity(pending.submitted),
        newTurn: currentTurnId
      };
      const notApplied = {
        ...guardrailTableRef.current,
        turn: detail.submittedTurn,
        action: pending.name,
        attempts: pending.attempts,
        heldForMs: detail.heldForMs
      };
      if (outcome === 'lost') {
        // The evidence trail. The engine logs nothing on this path, so this line
        // is the only record that the player acted and was folded regardless.
        console.warn('[PokerActions] action-lost', detail);
        reportActionNotApplied({ ...notApplied, outcome: 'lost' });
        triggerShowServerTimeout();
      } else if (pending.attempts > 1) {
        // Recovered after at least one re-send: worth a line, so the harness and
        // support can see how often delivery needed help. A first-try landing is
        // the normal case and stays silent.
        console.warn('[PokerActions] action-settled', detail);
        reportActionNotApplied({ ...notApplied, outcome: 'settled_after_retry' });
      }
      pendingRef.current = null;
    }
    clearRecoveryTimer();
    // ONLY when there is something to clear. This effect runs on EVERY
    // turn-identity change, which in live play is every frame - the engine
    // stamps `lastmove` on every turn advance and the identity includes it. A
    // setState to the value the state already holds is not free: React still
    // schedules a render pass before it bails out, so calling these two
    // unconditionally doubled this component's commits per frame on a table
    // where nothing is held at all. Measured with React's Profiler against
    // main: 60 changing frames on an idle table cost 60 commits before and
    // 118 after, on every mounted table for the whole session.
    //
    // Guarded on the state rather than on `pending` so the two cannot desync:
    // an `isActionInFlight` left true with no pending action is the row-stays-
    // hidden bug this whole branch exists to fix.
    if (isActionInFlight) setIsActionInFlight(false);
    if (heldWithoutSends) setHeldWithoutSends(false);
    // Same rule for the timer-off flag, and one more reason: it is a SHARED
    // hold, so Player.tsx sets it true to freeze the seat timer while a busted
    // player's rebuy countdown runs (it gates AddChipsModal). Clearing it
    // unconditionally here stole that hold (PR review finding 5).
    if (pending) setIsTimerOff(false);
  }, [
    currentTurnId,
    game.gameID,
    seatId,
    clearRecoveryTimer,
    setIsTimerOff,
    triggerShowServerTimeout,
    isActionInFlight,
    heldWithoutSends
  ]);

  // The action socket came back while an action was held. Re-assert this table
  // and re-send: the player already gave their input, and their connection
  // dropping is the failure they are least at fault for and most punished by.
  const prevActionPathRef = useRef(actionPathStatus);
  useEffect(() => {
    const cameBack = prevActionPathRef.current !== 'ok' && actionPathStatus === 'ok';
    prevActionPathRef.current = actionPathStatus;
    if (!cameBack || !pendingRef.current) return;
    // A real recovery is new information, so it earns another attempt instead of
    // being exempt from the budget - up to MAX_BUDGET, so a flapping socket
    // cannot raise the ceiling indefinitely.
    const raised = Math.min(pendingRef.current.budget + PATH_RECOVERY_BONUS, MAX_BUDGET);
    if (raised > pendingRef.current.budget) {
      pendingRef.current.budget = raised;
      // The budget is spendable again, so the "budget spent" line must be able
      // to fire again when it runs out - otherwise it is logged once per action
      // and every later exhaustion is invisible. Note that heldResyncsRef is
      // deliberately NOT reset here: the re-assert allowance is per action, and
      // resetting it per budget raise made the documented worst case wrong by
      // the number of bonuses (PR review).
      pendingRef.current.budgetSpentLogged = false;
      setHeldWithoutSends(false);
    }
    scheduleRecovery('path-recovered', true);
  }, [actionPathStatus, scheduleRecovery]);

  // The no-effect check. One retry interval after each send, if it is still our
  // turn and the turn state has not moved at all, the action did not land -
  // whatever the ack said. Re-assert the subscription and re-send, up to the
  // timer cap; after that, hold without adding traffic (the event-driven
  // triggers above still apply).
  useEffect(() => {
    if (!isActionInFlight) return undefined;
    const timeoutId = setTimeout(() => {
      const pending = pendingRef.current;
      if (!pending) return;
      if (!actionAppearsDropped(pending.submitted, currentTurnRef.current, isMyTurnRef.current)) {
        return;
      }
      console.warn('[PokerActions] action produced no effect - re-asserting table and re-sending', {
        gameID: game.gameID,
        action: pending.name,
        attempt: pending.attempts,
        turn: turnIdentity(pending.submitted)
      });
      scheduleRecovery('no-effect', true);
    }, actionRetryIntervalMs);
    return () => clearTimeout(timeoutId);
  }, [isActionInFlight, sendSeq, actionRetryIntervalMs, game.gameID, scheduleRecovery]);

  // The send budget is spent but the action is still held. Keep re-asserting
  // this table's subscription on a slow cadence, sending nothing.
  //
  // The two remedies are separate: the budget bounds how many times we ask the
  // engine to apply the action, and this bounds how many times we ask for the
  // table's feed back. Reaching the send cap with the turn still unchanged is
  // itself evidence the feed is the broken half - at the production clock the
  // budget spends at about the moment the server clock expires, so a turn
  // identity that has not moved by then means the frame saying so never arrived.
  //
  // Deliberately NOT the row coming back: the player's decision stands, and they
  // do not get to substitute another action. This only keeps the feed remedy
  // alive across the window before the per-table watchdog takes over, and then
  // stops. The allowance it spends is shared with the event-driven triggers, so
  // MAX_HELD_RESYNCS is the total per action however the re-asserts are
  // triggered (see resyncHeldTable).
  useEffect(() => {
    // A fresh action gets a fresh allowance. Reset here rather than on
    // heldWithoutSends going false, which also happens on every budget raise.
    if (!isActionInFlight) {
      heldResyncsRef.current = 0;
      return undefined;
    }
    if (!heldWithoutSends) return undefined;
    const intervalId = setInterval(() => {
      const pending = pendingRef.current;
      if (!pending) return;
      // Spent: stop the timer rather than waking every 10s to do nothing for
      // the rest of the turn (PR review).
      if (heldResyncsRef.current >= MAX_HELD_RESYNCS) {
        clearInterval(intervalId);
        return;
      }
      if (!actionAppearsDropped(pending.submitted, currentTurnRef.current, isMyTurnRef.current)) {
        return;
      }
      resyncHeldTable('held-interval');
    }, HELD_RESYNC_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isActionInFlight, heldWithoutSends, resyncHeldTable]);

  // The send budget is spent and the table has still not moved. Give the row
  // back one retry interval later, if nothing has changed by then.
  //
  // The delay is the retry interval rather than a new constant because it is the
  // same question the no-effect check asks: has a frame contradicted us yet? At
  // the production clock the budget spends at roughly 18s (six sends paced at
  // 3s), so the release lands at about 21s - just past the 17s server clock, so a
  // player whose action DID land almost always sees the turn move first and never
  // sees the row come back at all. What this catches is the client that cannot
  // see the turn move, where 21s is already far too long to leave someone with no
  // controls and no clock.
  //
  // The feed remedy is NOT what ends here. A dead feed is the per-table watchdog's
  // job (contexts/tableFeedWatchdog.ts), which now judges a table that lost its
  // feed across a reconnect and escalates to a fresh connection when its repairs
  // produce nothing - the two things missing when QA hit this.
  useEffect(() => {
    if (!isActionInFlight || !heldWithoutSends) return undefined;
    const timeoutId = setTimeout(
      () => releaseHeldAction('delivery-budget-spent'),
      actionRetryIntervalMs
    );
    return () => clearTimeout(timeoutId);
  }, [isActionInFlight, heldWithoutSends, actionRetryIntervalMs, releaseHeldAction]);

  // Nothing may outlive the component.
  useEffect(() => () => clearRecoveryTimer(), [clearRecoveryTimer]);

  useEffect(() => {
    if (actionsRef.current) {
      // Slide-up entrance via transform (translateY) rather than the `bottom`
      // layout property — composited, no layout/paint per frame (perf guide).
      gsap.fromTo(actionsRef.current, { y: 160 }, { y: 0, duration: 0.4, ease: 'power3.out' });
    }
  }, [isMyTurn]);

  const content = useMemo(() => {
    // EN-11023: no classic Straddle button. The straddle is a pre-action taken
    // during the hand before (StraddlePreActionButton, at the end of this row),
    // and no straddle decision carries into the next hand's dealer button and blinds.
    if (playerStatus === 4) {
      return null;
    }

    if (showCard) {
      return <ShowCard />;
    }

    if (muckCard) {
      return <MuckCard />;
    }

    if (!showAction || playerStack <= 0) {
      return null;
    }

    if (isMyTurn) {
      // Disabled as well as hidden while in flight: `visibility` takes the row
      // out of hit-testing for a real tap, and `disabled` makes the same true
      // for anything synthetic.
      const locked = isActionInFlight || isMaintenanceLocked;
      return (
        <>
          <Fold
            disabled={locked}
            submitAction={submitAction}
            onAckTimeout={onAckTimeout}
            triggerHideServerTimeout={triggerHideServerTimeout}
          />
          {call ? (
            <CallAction
              disabled={locked}
              submitAction={submitAction}
              onAckTimeout={onAckTimeout}
              triggerHideServerTimeout={triggerHideServerTimeout}
            />
          ) : (
            check && (
              <CheckAction
                disabled={locked}
                submitAction={submitAction}
                onAckTimeout={onAckTimeout}
                triggerHideServerTimeout={triggerHideServerTimeout}
              />
            )
          )}
          {(bet || raise) && !game.closedBetting && (
            <RaiseAction
              disabled={locked}
              submitAction={submitAction}
              onAckTimeout={onAckTimeout}
              triggerHideServerTimeout={triggerHideServerTimeout}
            />
          )}
        </>
      );
    } else {
      return (
        <>
          {preCall ? (
            <>
              <CheckboxAction
                neutralUntilSelected
                label='Fold'
                value={PLAYER_PRE_ACTION.FOLD}
                onChange={onClickPreSelectAction}
                checked={
                  preselectedAction === PLAYER_PRE_ACTION.FOLD ||
                  preselectedAction === PLAYER_PRE_ACTION.CHECK_OR_FOLD
                }
              />
              {allIn ? (
                <CheckboxAction
                  neutralUntilSelected
                  label={`All In ${
                    tableType === TABLE_TYPE_TOURNAMENT || tableType === TABLE_TYPE_SITNGO
                      ? toLocaleStringNumber(playerStack)
                      : currencyFormatter(playerStack)
                  }`}
                  value={PLAYER_PRE_ACTION.ALLIN}
                  onChange={onClickPreSelectAction}
                  checked={preselectedAction === PLAYER_PRE_ACTION.ALLIN}
                />
              ) : (
                playerCallAmount > 0 && (
                  <CheckboxAction
                    neutralUntilSelected
                    label={`Call ${
                      tableType === TABLE_TYPE_TOURNAMENT || tableType === TABLE_TYPE_SITNGO
                        ? toLocaleStringNumber(playerCallAmount)
                        : currencyFormatter(playerCallAmount)
                    }`}
                    value={PLAYER_PRE_ACTION.CALL}
                    onChange={onClickPreSelectAction}
                    checked={preselectedAction === PLAYER_PRE_ACTION.CALL}
                  />
                )
              )}
            </>
          ) : (
            <>
              <CheckboxAction
                neutralUntilSelected
                label='Check/Fold'
                value={PLAYER_PRE_ACTION.CHECK_OR_FOLD}
                onChange={onClickPreSelectAction}
                checked={preselectedAction === PLAYER_PRE_ACTION.CHECK_OR_FOLD}
              />
              <CheckboxAction
                neutralUntilSelected
                label='Check'
                value={PLAYER_PRE_ACTION.CHECK}
                onChange={onClickPreSelectAction}
                checked={preselectedAction === PLAYER_PRE_ACTION.CHECK}
              />
            </>
          )}
          {preCallAny && (
            <CheckboxAction
              neutralUntilSelected
              label='Call Any'
              value={PLAYER_PRE_ACTION.CALL_ANY}
              onChange={onClickPreSelectAction}
              checked={preselectedAction === PLAYER_PRE_ACTION.CALL_ANY}
            />
          )}
        </>
      );
    }
  }, [
    // The in-flight state decides whether these buttons are usable, so it MUST
    // be a dependency: without it the memo hands back a cached row built with a
    // stale value, and neither locking on click nor releasing afterwards ever
    // reaches the DOM. That is what made a released lock still read as disabled
    // for a whole turn - nothing else on the felt changes while the table waits
    // on this player, so nothing else invalidates the cache.
    isActionInFlight,
    isMaintenanceLocked,
    gameHand,
    playersStack,
    gameEnding,
    playerStatus,
    isMyTurn,
    showAction,
    showCard,
    muckCard,
    playerCallAmount,
    preselectedAction,
    playerTotalChips,
    players,
    seatId,
    preselectAction,
    notFoldedPlayers
  ]);

  // Hide the row (rather than unmount it) while an action is in flight, so the
  // footer keeps its height and nothing on the felt shifts.
  const hideActionRow = isMyTurn && isActionInFlight;

  const showsActionContent =
    !!content && !isCurrentUserFolded && !gameEnding && !runs.includes(MULTI_RUN_VOTING_PREFIX);
  // EN-11023: the "straddle next hand" chip ends the row, after Show/Muck Cards.
  // The server only offers it once this player's part in the hand is settled,
  // which is exactly when the gates above hide the rest of the row (folded, the
  // showdown, a vote they have no part in), so it is not held to them.
  const showsStraddleOffer = !!straddleOffer;

  return (
    <>
      <div className='player-actions'>
        <div
          ref={actionsRef}
          className='poker-actions'
          style={{
            // This div is the stacking context for the raise/bet panel, which
            // expands UP out of the footer over the felt. Its z must beat the
            // footer's history/chat icons (zIndex 15 in TopAction) so the panel
            // renders in front of them rather than behind.
            zIndex: 20,
            flexDirection: isPortrait ? 'column' : 'row',
            gap: isPortrait ? '0.25rem' : undefined,
            width: isPortrait ? '100%' : '100%',
            justifyContent: isPortrait ? undefined : 'center',
            position: 'relative'
          }}>
          {/* Hand strength handled by TopAction in landscape */}

          {/* Action buttons — centered */}
          {(showsActionContent || showsStraddleOffer) && (
            <div
              data-testid='poker-action-row'
              data-in-flight={hideActionRow ? 'true' : undefined}
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: isPortrait ? '0.25rem' : '0.5rem',
                alignItems: 'center',
                justifyContent: 'center',
                width: isPortrait ? '100%' : undefined,
                zIndex: 3,
                visibility: hideActionRow ? 'hidden' : 'visible'
              }}>
              {showsActionContent && content}
              {showsStraddleOffer && <StraddlePreActionButton />}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PokerActions;
